/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import {ProjectElement}          from './project.element';
import {ProjectElementType}      from './project.element.type';
import { BlobType }              from '../blob';

import { Project }               from '../project';

import { Logger }                from '@/util/logger';

import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {PropertiesHolder} from "@/models/properties.holder";
import {CorridorBatchElement} from "@/models/project/corridor.batch.element";
import * as $ from "jquery";
import {empty} from "@/util/util";
import {JSONBrowserMetadata} from "@/screens/ide/json.browser.metadata";
import {WindowType} from "@/screens/ide/window";
import {forkJoin} from "rxjs";
import {first, tap} from "rxjs/operators";
import {BatchSummaryCreator} from "@/helpers/batch.summary.creator";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";

/**
 *
 * @class BatchGroupElement
 *
 * Group of corridor batches.  When a Network Planner does want to do a simulation, they will
 * want to simulate a wide area of the CN rail network.  This can only be done in segments,
 * or "corridors".  Each corridor in turn covers several subsections of the network or "plants"
 * and a rail traffic demand in those plants.
 *
 * Note that "plant" is just a historical simulation term of art; simulation used to be mainly
 * about manufacturing plant setup, so the physical structure is typically called "the plant"...
 * even though it has nothing to do with rail operations.  It's just a term of art.
 *
 * The long and short of it is that when a network planner says "simulation" what they actually
 * mean is a batch of batches; each batch is a corridor (which can have NxM plant x traffic
 * combinations), these are the batches that we can run in our Scalable Work Engine.  These
 * batches then aggregate up into the batch group.  Which is the overall result the network
 * planner is looking for.
 *
 * How big?  Each corridor might have 300 simulations, say 5x60 (plant x traffic) where they
 * are trying out different traffic demands for different parts of the corridor.  With a
 * group of 5 or 10 corridors...it doesn't take long before you could be into 1000's of
 * simulations.
 *
 * That volume is not a problem per se, but it's a lot to process.  At Google (GCP), we can
 * push up to 1000 Cloud Run instances at a time, but after that (the quota limit), things
 * will start queuing up in Pub/Sub, and the system will start slowing down.  You can
 * potentially negotiate with Google to get more Cloud Run instances, but this will depend
 * on what physical hardware they have at a given datacenter.  You would like have to
 * make some kind of long term contract with them.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2022
 *
 */
@ProjectElementFactoryDecorator('BatchGroupElement')
export class BatchGroupElement extends ProjectElement {

  public batchActionKeys: string[] = [
    'kpiview',
    'kpidownload',
    'summarize',
    'renamebatch',
    'deletebatch',
    'viewinbrowser'
  ];

  /** the unique (UUID style) key for this batch group */
  public _key: string              = '';

  /** the path to the folder for the batch of batches */
  public _prefix: string           = '';

  /** standard constructor */
  constructor(
    project: Project,
         id: string,
     parent: ProjectElement,
      label: string  = 'Batch Group',
        key: string  = '',
     prefix: string  = '',
    publish: boolean = false
  ) {

    super(project, id, ProjectElementType.BatchGroup);

    this._acceptsBlobs.push(BlobType.None);

    this._enabled     = true;
    this._label       = label;
    this._key         = key;
    this._prefix      = prefix;
    this._icon        = 'fa fa-th-list';
    this._elementKind = 'Batch Group';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring BatchGroupElement");
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    return !this.isBusy();
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  /**
   * sub-classes must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.kpiview = {
      name: 'KPI View',
      icon: 'fas fa-keyboard',
      disabled: !this.hasKPI()
    };

    actionSet.kpidownload = {
      name: 'KPI (Download)',
      icon: 'fas fa-file-download',
      disabled: !this.hasKPI()
    };

    actionSet.summarize = {
      name: 'Summarize',
      icon: 'fas fa-file-csv',
      disabled: !this.hasKPI()
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.renamebatch = {
      name:     'Rename',
      icon:     'fas fa-tag',
      disabled:  this.isBusy()
    }

    actionSet.deletebatch = {
      name:     'Delete',
      icon:     'fas fa-trash-alt',
      disabled:  this.isBusy()
    };

    return actionSet;
  }

  /** test if any descendants have a KPI, we expect the children to be CorridorGroupElements */
  public hasKPI(): boolean {

    let found = false;

    this.children().forEach((item) => {

      const cbe: CorridorBatchElement = item as CorridorBatchElement;

      /* if all corridors that failed...we may in fact have no KPI for this batch of batches */

      if (cbe.hasKPI()) {

        /* there is at least one corridor that yeiled a KPI, so there is at least a partial KPI to be had */

        found = true;
      }

    });

    return found;
  }

  /** fetch (in memory) exactly 1 (the first found) KPI rollup for each corridor */
  public perCorridorKPI(deferred:  JQuery.Deferred<any>) {

    Logger.debug(`[BatchGroupElement] fetching all KPI...`);

    /* first gather the blob paths to the KPI */

    let blobKeys: string[] = [];

    this.children().forEach((item) => {

      const cbe: CorridorBatchElement = item as CorridorBatchElement;

      const key: string = cbe.kpiBlobPath();

      if (empty(key)) {

        return ;
      }

      blobKeys.push(key);

    });

    /* now grab them all, so we can form one aggregate KPI for the entire batch of batches */

    Logger.debug(`[BatchGroupElement] "downloading" (to GUI) KPI: `, blobKeys);

    /* fan out... */

    forkJoin(blobKeys.map(x => this._project.services.blobber.contents(x, 'latest', 'text').pipe(first()))).subscribe(
      (results: any[]) => {

        let merged: any = {};

        /*
         * each KPI object has properties which are the corridor name, which in turn has as its value,
         * the KPI rollup for that corridor.  So, at the batch of batches levels, we want those to all
         * be merged into the same object, so that the user has a single object of corridor results.
         *
         */

        results.forEach((item: any) => {

          merged = {...merged, ...JSON.parse(item)};
          delete merged.__meta__;

        });

        deferred.resolve(merged);
      },
      (error) => {

        deferred.reject(error);
      });

  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      key:    this._key,
      prefix: this._prefix
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    this._key    = node.key;
    this._prefix = node.prefix;
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('Key',    this._key);
    properties.set('Folder', this._prefix);

    /* add in the usual properties */

    return super.properties(properties);
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.batchActionKeys.find(x => x === key);
  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    /* do any dimension specific stuff */

    switch (key) {

      case 'summarize': {

          /*
           * create a new batch measure element, with this batch of batches as the initial
           * selection for the batch to use.
           *
           */

          new BatchSummaryCreator(this._project, '', this);

        }
        break;

      case 'kpiview': {

        /*
         * make a promise we can pass to perCorrirodKPI() becausue the contents must be
         * fetch asynchronously.
         *
         */

        const pending = $.Deferred();

        pending.then((results: any) => {

            Logger.debug('[BatchGroupElement] viewing all KPI: ', results);

            this._project.services.ide.launchWindow(
              new JSONBrowserMetadata(
              '',
              `All KPI: ${this._label}`,
              '',
              'direct',
              `All KPI: ${this._label}`,
               '<i class="fas fa-edit"></i>',
                WindowType.JSONBrowser,
                results
              )
            );

          },
          (error) => {

            Logger.error(`[BatchGroupElement] (view) could not fetch KPI content: `, error);

          });

        this.perCorridorKPI(pending);

      }
      break;

      case 'kpidownload': {
        /*
         * make a promise we can pass to perCorridorKPI() because the contents must be
         * fetch asynchronously.
         *
         */

        const pending = $.Deferred();

        pending.then((results: any) => {

            Logger.debug('[BatchGroupElement] downloading all KPI: ', results);

            /* serialize them into a blob ... */

            const text: string = JSON.stringify(results);
            const name: string = `${this._label}-kpi.json`;

            const blob: Blob   = new Blob([text], { type: 'application/json' });
            const url: string  = window.URL.createObjectURL(blob);

            /* set up a fake one-time link with the right download name and the blob attached as the download */

            const element: HTMLAnchorElement = document.createElement('a');
            element.href                     = url;
            element.download                 = name;
            element.style.display            = 'none';

            document.body.appendChild(element);

            /* download the blob to the user ... */

            element.click();

            /* clean up */

            document.body.removeChild(element);
          },
          (error) => {

            Logger.error(`[BatchGroupElement] (download) could not fetch KPI content: `, error);

          });

        this.perCorridorKPI(pending);
      }
      break;

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._key, this._key));

        break;
      }

      case 'renamebatch': {

        /* use a dialog to get the new name and then re-key any children... */

        const dim: BatchGroupElement = this;

        dim._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            Logger.debug('[BatchGroupElement] validating: ', name);

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Batch name can not be empty.');

              return;
            }

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = dim._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Batch of that name already exists.');

                return;
              }

            }

            resolve(name);

          },
          (name) => {

            /*
             * do the actual renaming...the dimension doesn't have a blob key, but child
             * blob keys might be dependent on the dimension name
             *
             */

            /* figure out what the path changes are ... */

            const parents: ProjectElement[] = this.ancestors().reverse();
            parents.shift();
            const search: string = dim._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${this._label}`;

            const replace: string = dim._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${name}`;

            Logger.debug(`[BatchGroupElement] renaming... ${search} => ${replace}...`);

            /* adjust (in bulk)... */

            this.shiftBlobs(search, replace, (result) => {

              if (result !== true) {

                /* there was a problem! */

                Logger.error(`[BatchGroupElement] could not properly rename children: `, result);

                return;
              }

              Logger.debug('[BatchGroupElement] children re-named, saving...');

              dim._label = name;

              dim.redraw();

              dim.save();

            });

          }, {
            title: 'Rename Batch',
            placeholder: this._label
          });

        return;
      }

      case 'deletebatch': {

        Logger.debug('[BatchGroupElement] deleting batch: ', this._label);

        this.deleteElements(selection);

        return;
      }
    }

  }


}
